import { PartnerType } from '@/api/types.ts';
import { RootState } from '@/app/store.ts';
import { Country } from '@arsnovasystems/ars-front-lib';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

type OnBoardingState = Omit<PartnerType, 'meansOfPayment'> & {
  onBoardingMode: 'invited' | 'to-migrate';
};

const initialState: OnBoardingState = {
  // UI related fields
  onBoardingMode: 'invited',

  // Partner related fields
  password: null,
  hasRunTour: false,
  name: '',
  email: '',
  companyVatNumber: '',
  address: '',
  country: null,
  stateRegion: '',
  town: '',
  zipCode: '',
  currency: '',
  accountingEmail: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  stripeId: '',
  defaultLang: '',
  type: 'reseller',
  signUpStatus: 'invited',
  hasPassword: false,
  file: undefined,
  wireTransferApproved: false,
};

const onBoardingSlice = createSlice({
  name: 'onBoarding',
  initialState,
  reducers: {
    setPassword: (state, action: PayloadAction<string>) => {
      state.password = action.payload;
    },
    setName: (state, action: PayloadAction<string>) => {
      state.name = action.payload;
    },
    setEmail: (state, action: PayloadAction<string>) => {
      state.email = action.payload;
    },
    setCompanyVatNumber: (state, action: PayloadAction<string>) => {
      state.companyVatNumber = action.payload;
    },
    setAddress: (state, action: PayloadAction<string>) => {
      state.address = action.payload;
    },
    setCountry: (state, action: PayloadAction<Country | null>) => {
      state.country = action.payload;
    },
    setStateRegion: (state, action: PayloadAction<string>) => {
      state.stateRegion = action.payload;
    },
    setTown: (state, action: PayloadAction<string>) => {
      state.town = action.payload;
    },
    setZipCode: (state, action: PayloadAction<string>) => {
      state.zipCode = action.payload;
    },
    setCurrency: (state, action: PayloadAction<string>) => {
      state.currency = action.payload;
    },
    setAccountingEmail: (state, action: PayloadAction<string>) => {
      state.accountingEmail = action.payload;
    },
    setFirstName: (state, action: PayloadAction<string>) => {
      state.firstName = action.payload;
    },
    setLastName: (state, action: PayloadAction<string>) => {
      state.lastName = action.payload;
    },
    setPhoneNumber: (state, action: PayloadAction<string>) => {
      state.phoneNumber = action.payload;
    },
    setFile: (state, action: PayloadAction<File | undefined>) => {
      state.file = action.payload;
    },
    setStripeId: (state, action: PayloadAction<string>) => {
      state.stripeId = action.payload;
    },
    setDefaultLang: (state, action: PayloadAction<string>) => {
      state.defaultLang = action.payload;
    },
    setAllPartnerData: (state, action: PayloadAction<PartnerType>) => {
      let accountingEmail = state.accountingEmail;
      if (!accountingEmail || accountingEmail === '') {
        accountingEmail = action.payload.accountingEmail;
        if (!accountingEmail || accountingEmail === '') {
          accountingEmail = action.payload.email;
        }
      }
      return { ...state, ...action.payload, accountingEmail };
    },

    setOnBoardingMode: (
      state,
      action: PayloadAction<'invited' | 'to-migrate'>,
    ) => {
      state.onBoardingMode = action.payload;
    },
  },
});

export const {
  setPassword,
  setName,
  setFile,
  setEmail,
  setCompanyVatNumber,
  setAddress,
  setCountry,
  setStateRegion,
  setTown,
  setZipCode,
  setCurrency,
  setAccountingEmail,
  setFirstName,
  setLastName,
  setPhoneNumber,
  setStripeId,
  setDefaultLang,
  setAllPartnerData,
  setOnBoardingMode,
} = onBoardingSlice.actions;

export default onBoardingSlice;

/**
 * Function getOnBoardingState
 *
 * @param state the root state
 * @returns     The state onBoarding
 */
export const getOnBoardingState = (state: RootState) => state.onBoarding;
